/**
 * Card order of the profile pages (ProfileCardGrid), pure functions without Vue or storage.
 *
 * A layout lists the movable cards per column, top to bottom. Fixed cards (e.g. Steckbrief top left, Protokoll last in the
 * middle) are never part of it. Cards the viewer may not see keep their place in the layout and are skipped when rendering,
 * so a later permission or a card that shows up again lands where it was.
 * Besides the order a layout holds the viewer's card states of the overview: `hidden` cards are not rendered at all (their
 * full view stays reachable through the tabs), `folded` cards start collapsed. Fixed top cards (Steckbrief) and cards with
 * `lockVisible` (To-dos) can neither be hidden nor folded. The states apply in every column layout, the order only in 3 columns.
 */
export type ProfileColumn = 'left' | 'main' | 'right';

export const PROFILE_COLUMNS: ProfileColumn[] = ['left', 'main', 'right'];

/** Mirror of `uiPreferences.profileLayouts.<page>` of the user-login-handler */
export interface ProfileLayout {
    left: string[];
    main: string[];
    right: string[];
    /** Cards the viewer hid on the overview */
    hidden?: string[];
    /** Cards that start collapsed */
    folded?: string[];
}

export type ProfileCardState = 'hidden' | 'folded';
export const PROFILE_CARD_STATES: ProfileCardState[] = ['hidden', 'folded'];

export interface ProfileGridCard {
    /** Stable key, also the slot name in ProfileCardGrid and the entry in the stored layout */
    key: string;
    /** Column in the default layout */
    column: ProfileColumn;
    /** Fixed place within its column: always first / always last, never movable */
    fixed?: 'top' | 'bottom';
    /** false: not shown for this viewer (permissions, person type); default true */
    visible?: boolean;
    /** Name for screen reader announcements while moving */
    title: string;
    /** Always shown and never folded (movable unless fixed), e.g. the To-dos */
    lockVisible?: boolean;
}

const isVisible = (card: ProfileGridCard) => card.visible !== false;

/** Whether the viewer may hide / fold the card */
export const canChangeState = (card: ProfileGridCard): boolean => card.fixed !== 'top' && !card.lockVisible;

/** Movable cards per column in the order of the card list */
export function defaultLayout(cards: ProfileGridCard[]): ProfileLayout {
    const layout: ProfileLayout = {left: [], main: [], right: []};
    cards.forEach((card) => {
        if (!card.fixed) {
            layout[card.column].push(card.key);
        }
    });
    return layout;
}

/**
 * Cleans a stored layout: unknown, fixed and duplicate keys are dropped, cards missing in it (new cards) are appended to
 * their default column – in the middle that is right before the fixed last card.
 */
export function normalizeLayout(saved: unknown, cards: ProfileGridCard[]): ProfileLayout {
    const byKey = new Map(cards.map((card) => [card.key, card]));
    const layout: ProfileLayout = {left: [], main: [], right: []};
    const seen = new Set<string>();
    if (saved && typeof saved === 'object') {
        PROFILE_COLUMNS.forEach((column) => {
            const keys = (saved as Record<string, unknown>)[column];
            (Array.isArray(keys) ? keys : []).forEach((key) => {
                const card = typeof key === 'string' ? byKey.get(key) : undefined;
                if (card && !card.fixed && !seen.has(card.key)) {
                    layout[column].push(card.key);
                    seen.add(card.key);
                }
            });
        });
    }
    cards.forEach((card) => {
        if (!card.fixed && !seen.has(card.key)) {
            layout[card.column].push(card.key);
        }
    });
    if (saved && typeof saved === 'object') {
        PROFILE_CARD_STATES.forEach((state) => {
            const keys = (saved as Record<string, unknown>)[state];
            const valid = (Array.isArray(keys) ? keys : []).filter((key, i, all) => {
                const card = typeof key === 'string' ? byKey.get(key) : undefined;
                return !!card && canChangeState(card) && all.indexOf(key) === i;
            }) as string[];
            if (valid.length) {
                layout[state] = valid;
            }
        });
    }
    return layout;
}

export const cardHasState = (layout: ProfileLayout, key: string, state: ProfileCardState): boolean =>
    (layout[state] || []).includes(key);

/** Sets or clears a card state; hiding clears folded and the other way round (a card has one state) */
export function setCardState(layout: ProfileLayout, key: string, state: ProfileCardState, on: boolean): ProfileLayout {
    const next = cloneLayout(layout);
    PROFILE_CARD_STATES.forEach((s) => {
        const list = (next[s] || []).filter((k) => k !== key);
        if (s === state && on) {
            list.push(key);
        }
        if (list.length) {
            next[s] = list;
        } else {
            delete next[s];
        }
    });
    return next;
}

export function sameLayout(a: ProfileLayout, b: ProfileLayout): boolean {
    const sameSet = (x: string[] = [], y: string[] = []) => x.length === y.length && x.every((key) => y.includes(key));
    return PROFILE_COLUMNS.every((column) => a[column].length === b[column].length && a[column].every((key, i) => b[column][i] === key))
        && PROFILE_CARD_STATES.every((state) => sameSet(a[state], b[state]));
}

export function cloneLayout(layout: ProfileLayout): ProfileLayout {
    const copy: ProfileLayout = {left: [...layout.left], main: [...layout.main], right: [...layout.right]};
    PROFILE_CARD_STATES.forEach((state) => {
        if (layout[state]?.length) {
            copy[state] = [...layout[state] as string[]];
        }
    });
    return copy;
}

/** The order of `order` with the card states of `states` (narrow layouts: default order, own states) */
export function withStates(order: ProfileLayout, states: ProfileLayout): ProfileLayout {
    const copy = cloneLayout({left: order.left, main: order.main, right: order.right});
    PROFILE_CARD_STATES.forEach((state) => {
        if (states[state]?.length) {
            copy[state] = [...states[state] as string[]];
        }
    });
    return copy;
}

export interface ColumnCards {
    top: ProfileGridCard[];
    movable: ProfileGridCard[];
    bottom: ProfileGridCard[];
}

/**
 * Visible cards of each column: fixed top, movable in layout order, fixed bottom. Hidden cards are left out unless
 * `includeHidden` (edit mode shows them as placeholders to show them again).
 */
export function columnCards(layout: ProfileLayout, cards: ProfileGridCard[], includeHidden = false): Record<ProfileColumn, ColumnCards> {
    const byKey = new Map(cards.map((card) => [card.key, card]));
    const shown = (card: ProfileGridCard) => isVisible(card) && (includeHidden || !cardHasState(layout, card.key, 'hidden'));
    const result = {} as Record<ProfileColumn, ColumnCards>;
    PROFILE_COLUMNS.forEach((column) => {
        result[column] = {
            top: cards.filter((card) => card.fixed === 'top' && card.column === column && shown(card)),
            movable: layout[column].map((key) => byKey.get(key)).filter((card): card is ProfileGridCard => !!card && shown(card)),
            bottom: cards.filter((card) => card.fixed === 'bottom' && card.column === column && shown(card)),
        };
    });
    return result;
}

/**
 * Moves `key` into `column` right before `beforeKey` (null: after the last movable card). Positions refer to the stored
 * layout, so hidden cards between visible ones stay where they are.
 */
export function moveCard(layout: ProfileLayout, key: string, column: ProfileColumn, beforeKey: string | null): ProfileLayout {
    const next = cloneLayout(layout);
    PROFILE_COLUMNS.forEach((c) => {
        next[c] = next[c].filter((k) => k !== key);
    });
    const index = beforeKey ? next[column].indexOf(beforeKey) : -1;
    if (index >= 0) {
        next[column].splice(index, 0, key);
    } else {
        next[column].push(key);
    }
    return next;
}

/**
 * Target of a move given as index in the visible movable cards of the target column (as drag & drop reports it):
 * the visible card that will follow the moved one, or null for the end.
 */
export function beforeKeyAt(visibleKeys: string[], movedKey: string, newIndex: number): string | null {
    const others = visibleKeys.filter((key) => key !== movedKey);
    return others[newIndex] ?? null;
}

/**
 * Keyboard move of a visible card: up/down within the column, left/right into the neighbour column at the same
 * position. null when the card cannot move further in that direction.
 */
export function keyboardMove(layout: ProfileLayout, cards: ProfileGridCard[], key: string,
                             direction: 'up' | 'down' | 'left' | 'right'): ProfileLayout | null {
    // hidden cards are placeholders in the edit mode and move like the others
    const visible = columnCards(layout, cards, true);
    const from = PROFILE_COLUMNS.find((column) => visible[column].movable.some((card) => card.key === key));
    if (!from) {
        return null;
    }
    const keys = visible[from].movable.map((card) => card.key);
    const pos = keys.indexOf(key);
    if (direction === 'up') {
        return pos > 0 ? moveCard(layout, key, from, keys[pos - 1]) : null;
    }
    if (direction === 'down') {
        if (pos >= keys.length - 1) {
            return null;
        }
        // directly behind the next visible card (hidden cards after it keep their place behind the moved one)
        const next = cloneLayout(layout);
        next[from] = next[from].filter((k) => k !== key);
        next[from].splice(next[from].indexOf(keys[pos + 1]) + 1, 0, key);
        return next;
    }
    const target = PROFILE_COLUMNS[PROFILE_COLUMNS.indexOf(from) + (direction === 'left' ? -1 : 1)];
    if (!target) {
        return null;
    }
    const targetKeys = visible[target].movable.map((card) => card.key);
    return moveCard(layout, key, target, targetKeys[pos] ?? null);
}
